'use client';
/* eslint-disable tailwindcss/no-arbitrary-value */
import { FeCarouselV2 } from '@hyphengroup/cms-components/components/FeCarousel';
import classNames from 'classnames';
import { usePathname } from 'next/navigation';
import React, { useCallback, useMemo } from 'react';
import ArrowRightAlt from '../../icons/ArrowRightAlt';
import { PageContentContainerType } from '../../PageContentContainer';
import {
  CategoryLinkPage,
  ComponentRpCategories,
} from '$/(cms)/_modules/cmsService/types';
import { getCmsPageLink } from '$/(cms)/_modules/routing';
import Button from '$/components/Button';
import { pushDataLayer } from '$/components/cms/pushDataLayer';
import { FeLink } from '$/components/FeLink';
import { useClientContext } from '$/modules/context/client';

export const RpCategories: React.FC<
  ComponentRpCategories & {
    ContentContainer?: PageContentContainerType;
  }
> = ({ links, ContentContainer = (p) => <div {...p} /> }) => {
  const { langPrefix, brand } = useClientContext();
  const pathname = usePathname();

  const checkIsCurrentPath = useCallback(
    (page: CategoryLinkPage) => {
      const { slug } = page ?? {};
      if (!slug) {
        return false;
      }
      return pathname?.endsWith(slug) ?? false;
    },
    [pathname],
  );

  const openPage = (
    linkTitle: string,
    page: CategoryLinkPage,
    index: number,
  ) => {
    if (!page) {
      return;
    }
    pushDataLayer({
      event: 'navigate',
      component_type: 'tab',
      component_title: `Header Section/Tab_${linkTitle}`,
      action: 'click',
      index,
    });
  };

  // Logic for putting selected category into the first place
  const selectedCategoryIndex = useMemo(() => {
    return (
      links?.findIndex((link) => link?.page && checkIsCurrentPath(link.page)) ??
      -1
    );
  }, [links, checkIsCurrentPath]);

  const items = useMemo(() => {
    if (!links?.length) {
      return [];
    }
    if (selectedCategoryIndex > 0) {
      const selectedItem = links[selectedCategoryIndex];
      const reorderedLinks = [
        selectedItem,
        ...links.slice(0, selectedCategoryIndex),
        ...links.slice(selectedCategoryIndex + 1),
      ];
      return reorderedLinks;
    }

    return links;
  }, [links, selectedCategoryIndex]);

  return (
    /*
    this block should be full width to allow FECarrousel with prop fullscreenOnMobile
    to bleed to the edges of the screen on mobile
    that's why we need 'overflow-hidden mx-auto w-full'
    */
    <div className="overflow-hidden">
      <ContentContainer>
        <FeCarouselV2
          navigation={{
            showOnDesktop: true,
            prevElClassName: classNames('left-0 -translate-x-1/2'),
            nextElClassName: classNames('right-0 translate-x-1/2'),
            prevElement: <CustomNavButtom orient="left" />,
            nextElement: <CustomNavButtom orient="right" />,
            disabledClassName: '!hidden',
          }}
          fullscreenOnMobile
        >
          {items!.map((link, index) => {
            if (!link.page) {
              return null;
            }
            const isCurrentPath = checkIsCurrentPath(link.page);
            if (link?.page?.section) {
              return (
                <FeLink
                  draggable={false}
                  key={link.label}
                  href={getCmsPageLink({
                    pageType: 'page',
                    vertical: link.page.section,
                    slug: link.page.slug,
                    langPrefix,
                    brand,
                    urlPrefix: link.page.urlPrefix,
                  })}
                >
                  <Button
                    styleType="neutral-light-and-blue"
                    variant="outline"
                    selected={isCurrentPath}
                    className="justify-start"
                    title={link.label}
                    onClick={() => {
                      if (isCurrentPath) {
                        return;
                      }
                      openPage(link.label, link.page!, index);
                    }}
                  >
                    <div className="overflow-hidden text-ellipsis">
                      {link!.label!}
                    </div>
                  </Button>
                </FeLink>
              );
            }
          })}
        </FeCarouselV2>
      </ContentContainer>
    </div>
  );
};

const CustomNavButtom = (props: { orient: 'left' | 'right' }) => {
  return (
    <div className={classNames('relative')}>
      <div
        className={classNames(
          'absolute -z-1 size-[--button-size-regular]',
          { 'left-1/2 -translate-x-full': props.orient === 'right' },
          { 'right-1/2 translate-x-full': props.orient === 'left' },
          'top-1/2 -translate-y-1/2',
          'bg-gradient-to-r from-[rgba(255,255,255,0)] to-[var(--fill-base)]',
          { 'rotate-180': props.orient === 'left' },
        )}
      />
      <div
        className={classNames(
          'size-[--button-size-small]',
          'border border-solid border-[--borders-default]',
          'rounded-[1000px]',
          'shadow-md bg-buttons/base',
          'flex items-center justify-center',
        )}
      >
        <ArrowRightAlt
          className={classNames('size-5 shrink-0', {
            'rotate-180': props.orient === 'left',
          })}
        />
      </div>
    </div>
  );
};
